import {Helmet} from 'react-helmet-async';
import {filter} from 'lodash';
import React, {useEffect, useState} from 'react';
import {useNavigate} from "react-router-dom";
// @mui
import {
    Avatar,
    Button,
    Card,
    Container,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography
} from '@mui/material';
// components
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Label from '../components/label';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
// sections
import {UserListHead} from '../sections/@dashboard/user';
import {config} from "../constant";
import ContextMenu from "../components/context-menu/ContextMenu";
import DialogUserNominations from "../sections/@dashboard/user/DialogUserNominations";
import DialogEditPerson from "../sections/@dashboard/user/DialogEditPerson";

// ----------------------------------------------------------------------

const TABLE_HEAD = [
    {id: 'name', label: 'Nom', alignRight: false, sortable: true},
    {id: 'email', label: 'E-mail', alignRight: false, sortable: false},
    {id: 'status', label: 'Nominations', alignRight: false, sortable: false},
    {id: ''}
];

// ----------------------------------------------------------------------

const descendingComparator = (a, b, orderBy) => {
    const valueA = orderBy === 'name' ? `${a.firstName} ${a.lastName}` : a[orderBy];
    const valueB = orderBy === 'name' ? `${b.firstName} ${b.lastName}` : b[orderBy];

    if (valueB < valueA) {
        return -1;
    }
    if (valueB > valueA) {
        return 1;
    }
    return 0;
};

const getComparator = (order, orderBy) => {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
};

const applySortFilter = (array, comparator, query) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    if (query) {
        return filter(array, (_user) => _user.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
    }
    return stabilizedThis.map((el) => el[0]);
};

export default function UserPage() {
    const navigate = useNavigate();
    const [userContextMenu, setUserContextMenu] = useState(null);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');
    const [users, setUsers] = useState(null);
    const [user, setUser] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [nominationsDialogOpen, setNominationsDialogOpen] = useState(false);

    useEffect(() => {
        updateList();
    }, []);

    const updateList = () => {
        fetch(`${config.BASE_URL}/people/nominations`)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`${response.status} ${response.statusText}`)
                }
                return response.json();
            }).then(data => {
                setUsers(applySortFilter(data, getComparator(order, orderBy), null));
            }).catch(error => {
                const message = 'Erreur dans la récupération des utilisateurs';
                console.error(message, error);
                navigate("/error", {state: {message: message}});
            });
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy(property);

        setUsers(applySortFilter(users, getComparator(newOrder, property), null));
    };

    const deleteUser = () => {
        const requestOptions = {
            method: 'DELETE',
            headers: {'Content-Type': 'application/json'}
        };
        fetch(`${config.BASE_URL}/people/${user.id}`, requestOptions)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`${response.status} ${response.statusText}`)
                }
                updateList();
            }).catch(error => {
                const message = 'Erreur dans la suppression d\'un utilisateur';
                console.error(message, error);
                navigate("/error", {state: {message: message}});
            });
    };

    const showNominations = (user) => {
        setUser(user);
        setNominationsDialogOpen(true);
    };

  // const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - USERLIST.length) : 0;
  // const filteredUsers = applySortFilter(USERLIST, getComparator(order, orderBy), filterName);
  // const isNotFound = !filteredUsers.length && !!filterName;

    const renderNominations = (user) => {
        switch (user.nominations.length) {
            case 0:
                return (<Label color='warning' sx={{textTransform: 'initial'}}>Aucune nomination</Label>);
            case 1:
                return (<Label color='success' sx={{textTransform: 'initial', cursor: 'pointer'}} onClick={() => showNominations(user)}>{user.nominations.length} nomination</Label>);
            default:
                return (<Label color='success' sx={{textTransform: 'initial', cursor: 'pointer'}} onClick={() => showNominations(user)}>{user.nominations.length} nominations</Label>);
        }
    };

    return (
        <>
            <Helmet>
                <title>Compétiteurs | Lose Awards</title>
            </Helmet>

            <Container maxWidth={false}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                    <Typography variant="h4" gutterBottom>Compétiteurs</Typography>
                    <Button
                        variant="contained"
                        startIcon={<Iconify icon="eva:plus-fill" />}
                        onClick={() => {
                            setUser(null);
                            setDialogOpen(true);
                        }}
                    >
                        Ajouter
                    </Button>
                </Stack>
                <Card>
                    {users ? (
                        <Scrollbar>
                            <TableContainer sx={{ minWidth: 800 }}>
                                <Table>
                                    <UserListHead
                                        order={order}
                                        orderBy={orderBy}
                                        headLabel={TABLE_HEAD}
                                        onRequestSort={handleRequestSort}
                                    />
                                    <TableBody>
                                        {users.map((row, index) => {
                                            const {id, firstName, lastName, email, avatar} = row;
                                            const avatarUrl = avatar ? `data:${avatar.contentType};base64,${avatar.data}` : '/assets/images/avatars/avatar_default.jpg';
                                            return (
                                                <TableRow hover key={id} tabIndex={-1} role="checkbox">
                                                    <TableCell component="th" scope="row" padding="normal">
                                                        <Stack direction="row" alignItems="center" spacing={2}>
                                                            <Avatar alt={firstName} src={avatarUrl} />
                                                            <Typography variant="subtitle2" noWrap>{firstName} {lastName}</Typography>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell align="left">{email}</TableCell>
                                                    <TableCell align="left">{renderNominations(row)}</TableCell>
                                                    <TableCell align="right">
                                                        <IconButton
                                                            size="large"
                                                            color="inherit"
                                                            onClick={event => {
                                                                setUser(row);
                                                                setUserContextMenu(event.currentTarget);
                                                            }}
                                                        >
                                                            <Iconify icon={'eva:more-vertical-fill'} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {/*{emptyRows > 0 && (*/}
                                        {/*    <TableRow style={{ height: 53 * emptyRows }}>*/}
                                        {/*      <TableCell colSpan={6} />*/}
                                        {/*    </TableRow>*/}
                                        {/*)}*/}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Scrollbar>
                    ) : (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="20vh">
                            <CircularProgress />
                        </Box>
                    )}
                </Card>
            </Container>

            <ContextMenu
                target={userContextMenu}
                setTarget={setUserContextMenu}
                handleEdit={() => setDialogOpen(true)}
                handleDelete={deleteUser}
            >
            </ContextMenu>

            {dialogOpen && (
                <DialogEditPerson
                    open={dialogOpen}
                    onClose={refresh => {
                        setDialogOpen(false);
                        if (refresh) {
                            updateList();
                        }
                    }}
                    person={user}
                    setPerson={setUser}
                >
                </DialogEditPerson>
            )}

            {nominationsDialogOpen && (
                <DialogUserNominations
                    open={nominationsDialogOpen}
                    onClose={() => setNominationsDialogOpen(false)}
                    personWithNominations={user}>
                </DialogUserNominations>
            )}
        </>
    );
}
